function throttle(fn, delay) {
  let last = 0;
  return (...args) => {
    const now = Date.now();
    if (now - last >= delay) {
      last = now;
      fn(...args);
    }
  };
}

/**
 * <tqc-carousel>
 *
 * Controls for swapping a single "active" item in a row of things.
 *
 * The controls are hidden with CSS on larger screens.
 */
class TqcCarousel extends HTMLElement {
  // Keep this magic number in sync with the negative margin in .tqc-carousel-row
  NEGATIVE_MARGIN_BUFFER = 24;

  constructor() {
    super();
    this.currentIndex = 0;
  }

  connectedCallback() {
    this.row = this.querySelector(".tqc-carousel-row");
    this.items = Array.from(this.querySelectorAll(".tqc-carousel-item"));
    this.dots = Array.from(this.querySelectorAll(".tqc-carousel-dots button"));

    if (!this.row || this.items.length === 0 || this.dots.length === 0) return;

    this.dots.forEach((dot, index) => {
      dot.addEventListener("click", (e) => {
        e.preventDefault();
        this.showSlide(index);
      });
    });

    // throttle scroll events to reduce CPU impact of
    // repeated getBoundingClientRect calls on scroll
    this.row.addEventListener(
      "scroll",
      throttle(() => this.handleScroll(), 50),
      { passive: true },
    );
  }

  handleScroll = () => {
    const containerLeft = this.row.getBoundingClientRect().left;

    let minDistance = Infinity;
    let activeIndex = 0;

    this.items.forEach((item, index) => {
      // Find the item with the smallest distance between its left edge
      // and the left edge of the scroll container
      const itemLeft = item.getBoundingClientRect().left;
      const distance = Math.abs(
        itemLeft - containerLeft - this.NEGATIVE_MARGIN_BUFFER,
      );

      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = index;
      }
    });

    this.items.forEach((item, index) => {
      item.classList.toggle("tqc-carousel--active", index === activeIndex);
    });
    this.updateActiveUiElements(activeIndex);
  };

  updateActiveUiElements(newIndex) {
    this.currentIndex = newIndex;
    this.items.forEach((item, i) => {
      item.classList.toggle("tqc-carousel--active", i === newIndex);
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle("tqc-carousel--active", i === newIndex);
    });
  }

  showSlide(index) {
    const target = this.items[index];
    if (!target) return;

    const containerLeft = this.row.getBoundingClientRect().left;
    const targetLeft = target.getBoundingClientRect().left;

    const scrollOffset = targetLeft - containerLeft;

    this.row.scrollTo({
      left: this.row.scrollLeft + scrollOffset - this.NEGATIVE_MARGIN_BUFFER,
      behavior: "smooth",
    });
  }
}

customElements.define("tqc-carousel", TqcCarousel);
